import { useEffect, useRef, useState, FormEvent } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { carUrl } from '../lib/slugId';
import { useAuth } from '../auth/AuthContext';
import { PageHeader } from '../components/PageHeader';
import type { Conversation, Message, SingleResponse } from '../api/types';

export function ConversationView() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    setTimeout(() => {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
    }, 50);
  };

  useEffect(() => {
    if (!conversationId) return;
    apiFetch<SingleResponse<Conversation>>(`/api/v1/conversations/${conversationId}`)
      .then((res) => {
        setConversation(res.data);
        scrollToBottom();
        // Mark as read on open
        apiFetch(`/api/v1/conversations/${conversationId}/read`, { method: 'POST' }).catch(
          () => undefined
        );
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [conversationId]);

  const handleSend = async (e: FormEvent) => {
    e.preventDefault();
    if (!body.trim() || !conversationId) return;
    setSending(true);
    try {
      const res = await apiFetch<SingleResponse<Message>>(
        `/api/v1/conversations/${conversationId}/messages`,
        {
          method: 'POST',
          body: JSON.stringify({ body: body.trim() }),
        }
      );
      setConversation((prev) =>
        prev
          ? {
              ...prev,
              messages: [...(prev.messages ?? []), res.data],
              last_message_at: res.data.created_at,
            }
          : prev
      );
      setBody('');
      scrollToBottom();
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !conversation) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error || t('inbox.notFound')}</Alert>
      </Container>
    );
  }

  const otherParticipantName = conversation.other_participant?.name ?? '—';
  const carLabel = conversation.car?.display_name ?? '—';

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 4 } }}>
      <PageHeader
        crumbs={[
          { label: t('inbox.title'), to: '/inbox' },
          { label: t('inbox.aboutCar', { car: carLabel }) },
        ]}
      />
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar src={conversation.other_participant?.avatar_url ?? undefined}>
            {otherParticipantName[0]}
          </Avatar>
          <Box>
            <Typography variant="h5">{otherParticipantName}</Typography>
            {conversation.car && (
              <Typography
                variant="body2"
                color="text.secondary"
                component={RouterLink}
                to={carUrl(conversation.car)}
                sx={{ textDecoration: 'none' }}
              >
                {t('inbox.aboutCar', { car: carLabel })}
              </Typography>
            )}
          </Box>
        </Stack>

        <Paper
          ref={scrollRef}
          variant="outlined"
          sx={{
            p: 2,
            height: { xs: 380, md: 500 },
            overflowY: 'auto',
            bgcolor: 'background.default',
          }}
        >
          <Stack spacing={1.5}>
            {(conversation.messages ?? []).map((m) => {
              const isMine = m.user_id === user?.id;
              return (
                <Box
                  key={m.id}
                  sx={{
                    display: 'flex',
                    justifyContent: isMine ? 'flex-end' : 'flex-start',
                  }}
                >
                  <Paper
                    sx={{
                      p: 1.5,
                      maxWidth: '75%',
                      bgcolor: isMine ? 'primary.main' : 'background.paper',
                      color: isMine ? 'primary.contrastText' : 'text.primary',
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {m.body}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.7, display: 'block', mt: 0.5 }}
                    >
                      {new Date(m.created_at).toLocaleString()}
                    </Typography>
                  </Paper>
                </Box>
              );
            })}
          </Stack>
        </Paper>

        <Box component="form" onSubmit={handleSend}>
          <Stack direction="row" spacing={1} alignItems="flex-end">
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder={t('inbox.sendPlaceholder')}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={sending}
            />
            <Button
              type="submit"
              variant="contained"
              endIcon={<SendIcon />}
              disabled={sending || !body.trim()}
            >
              {sending ? t('inbox.sending') : t('inbox.send')}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
}
